import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { Product } from 'src/app/models/woocommerce-product.model';
import { WooCommerceService } from 'src/app/services/woocommerce.service';
import { EventsService } from 'src/app/services/events.service';

@Component({
  selector: 'page-store',
  templateUrl: 'store.page.html',
  styleUrls: ['store.page.scss'],
  standalone: false
})
export class StorePage implements OnInit {
  public page$: Observable<any>;
  public products$: Observable<Product[]>;
  public loading = true;
  public error = false;
  /** Factor de conversión para mostrar precios en puntos (API). */
  conversionFactor = 1;
  /** Nombre de la unidad de puntos (API). */
  pointsName = '';

  constructor(
    private router: Router,
    private woocommerceService: WooCommerceService,
    private events: EventsService,
    private translate: TranslateService
  ) {}

  ngOnInit() {
    this.loadProducts();
  }

  ionViewWillEnter() {
    this.loadProducts();
  }

  loadProducts() {
    this.loading = true;
    this.error = false;
    const cached = this.woocommerceService.getCachedStoreUserInfo();
    if (cached) {
      this.conversionFactor = this.woocommerceService.getConversionFactorFromUserInfo(cached);
      this.pointsName = this.woocommerceService.getPointsNameFromUserInfo(cached);
    }
    this.page$ = this.woocommerceService.getStoreUserInfo().pipe(
      tap((res: any) => {
        this.conversionFactor = this.woocommerceService.getConversionFactorFromUserInfo(res);
        this.pointsName = this.woocommerceService.getPointsNameFromUserInfo(res);
      }),
      map(() => ({ title: this.translate.instant('STORE-TITLE') })),
      tap((page) => this.events.pages$$.next(page.title)),
      catchError(() => {
        this.error = true;
        this.loading = false;
        return of({ title: this.translate.instant('STORE-TITLE') });
      }),
      shareReplay()
    );
    this.products$ = this.page$.pipe(
      switchMap(() => this.woocommerceService.getProducts()),
      map((res: any) => res?.products ?? (Array.isArray(res) ? res : [])),
      tap(() => { this.loading = false; }),
      catchError(() => {
        this.error = true;
        this.loading = false;
        return of([]);
      }),
      shareReplay()
    );
  }

  /** True si las variaciones del producto se diferencian por precio (opciones numéricas), no por talla/color. */
  isVariableByPrice(product: Product): boolean {
    const variationAttr = product.attributes?.find(a => a.variation);
    if (!variationAttr?.options?.length) return false;
    return variationAttr.options.every(opt => !isNaN(parseFloat(opt)) && isFinite(Number(opt)));
  }

  /** Precio mínimo entre las variaciones de un producto variable. */
  private getMinVariationPrice(product: Product): number | null {
    // Si las variaciones son objetos completos, usar su precio directamente
    if (product.variations?.length && typeof product.variations[0] === 'object') {
      const prices = (product.variations as any[])
        .map(v => v.price_in_points ?? (v.price != null ? parseFloat(String(v.price)) : null))
        .filter((p): p is number => p != null && !isNaN(p) && p > 1);
      if (prices.length) return Math.min(...prices);
    }
    // Fallback: las opciones del atributo de variación (disponibles en el listado de catálogo)
    const variationAttr = product.attributes?.find(a => a.variation);
    if (variationAttr?.options?.length) {
      const prices = variationAttr.options
        .map(opt => parseFloat(opt))
        .filter(p => !isNaN(p) && p > 1);
      if (prices.length) return Math.min(...prices);
    }
    return null;
  }

  /** Puntos a mostrar (precio × factor de conversión). Para variables por precio, usa el precio mínimo de variación. */
  getDisplayPoints(product: Product): number | null {
    if (this.isVariableByPrice(product)) {
      const min = this.getMinVariationPrice(product);
      if (min == null) return null;
      return Math.round(min * this.conversionFactor);
    }
    const base = product.price_in_points ?? (product.price != null ? parseFloat(String(product.price)) : null);
    if (base == null || isNaN(Number(base)) || Number(base) === 1) return null;
    return Math.round(Number(base) * this.conversionFactor);
  }

  /** True si el precio del producto es 1 y no debe mostrarse. */
  isPriceHidden(product: Product): boolean {
    const base = product.price_in_points ?? (product.price != null ? parseFloat(String(product.price)) : null);
    return Number(base) === 1;
  }

  goToProductDetail(product: Product) {
    this.router.navigate(['store', 'product', product.id]);
  }

  goToOrders() {
    this.router.navigate(['store', 'orders']);
  }

  refresh(event?: any) {
    this.loadProducts();
    if (event?.target?.complete) {
      event.target.complete();
    }
  }
}
